// src/modules/aiml/TensorFlowSection.jsx
// TensorFlow.js — MobileNet, COCO-SSD, MoveNet, custom training, transfer learning
// Tabs: overview | mobilenet | coco_ssd | posenet | custom_train | transfer | layers | concepts

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell,
} from 'recharts';
import { ModelLoader, WebGPUBadge, InferenceTag } from './AIMLModule.jsx';

const ACCENT = '#f97316';
import { _livePalette as P } from '../../context/paletteSync';
const TABS = ['overview','mobilenet','coco_ssd','posenet','custom_train','transfer','layers','concepts'];

const Btn = ({ onClick, children, color = ACCENT, disabled, style }) => (
  <button onClick={onClick} disabled={disabled} style={{ padding: '8px 18px', background: color + '18', border: `1px solid ${color}`, color, fontSize: '10px', cursor: disabled ? 'not-allowed' : 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', fontWeight: 700, opacity: disabled ? 0.4 : 1, ...style }}>{children}</button>
);
const Tag = ({ children, color = ACCENT }) => (
  <span style={{ padding: '2px 8px', background: color + '20', border: `1px solid ${color}44`, color, fontSize: '9px', borderRadius: '2px', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>{children}</span>
);
const Panel = ({ title, accent = ACCENT, children, style }) => (
  <div style={{ background: 'var(--surface)', border: '1px solid var(--b0)', borderRadius: '4px', overflow: 'hidden', ...style }}>
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--b0)', fontSize: '9px', letterSpacing: '.1em', color: accent, fontWeight: 700, fontFamily: 'JetBrains Mono' }}>{title}</div>
    <div style={{ padding: '14px' }}>{children}</div>
  </div>
);

// ── TF.js CDN loader ──────────────────────────────────────────────
const TF_CDN   = 'https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.22.0/dist/tf.min.js';
const MN_CDN   = 'https://cdn.jsdelivr.net/npm/@tensorflow-models/mobilenet@2.1.1/dist/mobilenet.min.js';
const COCO_CDN = 'https://cdn.jsdelivr.net/npm/@tensorflow-models/coco-ssd@2.2.3/dist/coco-ssd.min.js';
const POSE_CDN = 'https://cdn.jsdelivr.net/npm/@tensorflow-models/pose-detection@2.1.3/dist/pose-detection.min.js';

let _tfLoaded = false;
async function ensureTF() {
  if (_tfLoaded && window.tf) return;
  await loadScript(TF_CDN);
  _tfLoaded = true;
}
function loadScript(src) {
  return new Promise((res, rej) => {
    if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
    const s = document.createElement('script');
    s.src = src; s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });
}
async function ensureModel(cdn) { await ensureTF(); await loadScript(cdn); }

// ── SKELETON DRAWING ──────────────────────────────────────────────
const POSE_CONNECTIONS = [
  ['nose','left_eye'],['nose','right_eye'],['left_eye','left_ear'],['right_eye','right_ear'],
  ['left_shoulder','right_shoulder'],['left_shoulder','left_elbow'],['right_shoulder','right_elbow'],
  ['left_elbow','left_wrist'],['right_elbow','right_wrist'],
  ['left_shoulder','left_hip'],['right_shoulder','right_hip'],['left_hip','right_hip'],
  ['left_hip','left_knee'],['right_hip','right_knee'],['left_knee','left_ankle'],['right_knee','right_ankle'],
];

function drawSkeleton(canvas, keypoints, imgW, imgH) {
  const ctx = canvas.getContext('2d');
  const scX = canvas.width / imgW, scY = canvas.height / imgH;
  const kpMap = {};
  keypoints.forEach(kp => { kpMap[kp.name] = kp; });

  POSE_CONNECTIONS.forEach(([a, b]) => {
    const ka = kpMap[a], kb = kpMap[b];
    if (!ka || !kb || ka.score < 0.3 || kb.score < 0.3) return;
    ctx.beginPath();
    ctx.moveTo(ka.x * scX, ka.y * scY);
    ctx.lineTo(kb.x * scX, kb.y * scY);
    ctx.strokeStyle = '#34d399'; ctx.lineWidth = 2; ctx.stroke();
  });
  keypoints.forEach(kp => {
    if (kp.score < 0.3) return;
    ctx.beginPath();
    ctx.arc(kp.x * scX, kp.y * scY, 5, 0, Math.PI * 2);
    ctx.fillStyle = '#f59e0b'; ctx.fill();
  });
}

// ── SIMPLE CUSTOM NN (pure JS, no TF.js needed for demo) ──────────
const sigmoid = x => 1 / (1 + Math.exp(-x));
class CustomNN {
  constructor(inputDim, hiddenDim, outputDim, lr = 0.1) {
    const r = () => (Math.random() - 0.5) * 0.5;
    this.w1 = Array.from({ length: hiddenDim }, () => Array.from({ length: inputDim }, r));
    this.b1 = Array.from({ length: hiddenDim }, r);
    this.w2 = Array.from({ length: outputDim }, () => Array.from({ length: hiddenDim }, r));
    this.b2 = Array.from({ length: outputDim }, r);
    this.lr = lr;
  }
  forward(x) {
    this.x = x;
    this.h = this.w1.map((row, i) => Math.max(0, row.reduce((s, w, j) => s + w * x[j], this.b1[i])));
    this.hRaw = this.w1.map((row, i) => row.reduce((s, w, j) => s + w * x[j], this.b1[i]));
    this.z = this.w2.map((row, i) => row.reduce((s, w, j) => s + w * this.h[j], this.b2[i]));
    const mx = Math.max(...this.z);
    const exps = this.z.map(v => Math.exp(v - mx));
    const sum = exps.reduce((a, b) => a + b, 0);
    this.out = exps.map(v => v / sum);
    return this.out;
  }
  step(x, y) { // y is one-hot
    const out = this.forward(x);
    const dz = out.map((o, i) => o - y[i]);
    this.w2 = this.w2.map((row, i) => row.map((w, j) => w - this.lr * dz[i] * this.h[j]));
    this.b2 = this.b2.map((b, i) => b - this.lr * dz[i]);
    const dh = this.h.map((_, j) => this.w2.reduce((s, row, i) => s + row[j] * dz[i], 0));
    const dhReLU = dh.map((d, j) => this.hRaw[j] > 0 ? d : 0);
    this.w1 = this.w1.map((row, i) => row.map((w, j) => w - this.lr * dhReLU[i] * x[j]));
    this.b1 = this.b1.map((b, i) => b - this.lr * dhReLU[i]);
    return -Math.log(Math.max(1e-7, out[y.indexOf(1)]));
  }
}

// ═════════════════════════════════════════════════════════════════
export default function TensorFlowSection({ gpuInfo }) {
  const [tab, setTab] = useState('overview');

  // MobileNet
  const [mnStatus, setMnStatus]   = useState('idle');
  const [mnProgress, setMnProgress] = useState(0);
  const [mnResult, setMnResult]   = useState(null);
  const [mnTime, setMnTime]       = useState(null);
  const [mnImgSrc, setMnImgSrc]   = useState(null);
  const mnRef = useRef(null);

  // COCO-SSD
  const [cocoStatus, setCocoStatus]   = useState('idle');
  const [cocoProgress, setCocoProgress] = useState(0);
  const [cocoResult, setCocoResult]   = useState(null);
  const [cocoTime, setCocoTime]       = useState(null);
  const [cocoImgSrc, setCocoImgSrc]   = useState(null);
  const cocoRef  = useRef(null);
  const cocoCanvas = useRef(null);

  // Pose
  const [poseStatus, setPoseStatus]   = useState('idle');
  const [poseProgress, setPoseProgress] = useState(0);
  const [poseResult, setPoseResult]   = useState(null);
  const [poseTime, setPoseTime]       = useState(null);
  const [poseImgSrc, setPoseImgSrc]   = useState(null);
  const poseRef   = useRef(null);
  const poseCanvas = useRef(null);

  // Custom train
  const [ctDataset, setCtDataset] = useState('spiral');
  const [ctStatus, setCtStatus]   = useState('idle');
  const [ctLoss, setCtLoss]       = useState([]);
  const [ctAcc, setCtAcc]         = useState([]);
  const [ctEpoch, setCtEpoch]     = useState(0);
  const [ctHidden, setCtHidden]   = useState(8);
  const [ctLR, setCtLR]           = useState(0.05);
  const ctRAF = useRef(null);
  const ctNetRef = useRef(null);

  // Transfer
  const [tfStatus, setTfStatus]   = useState('idle');
  const [tfClasses, setTfClasses] = useState([{ name:'Class A', samples:[] },{ name:'Class B', samples:[] }]);
  const [tfResult, setTfResult]   = useState(null);
  const tfImgRef = useRef(null);

  // Layers
  const [layerDef, setLayerDef]   = useState([
    { type:'Conv2D', filters:32, kernel:3, act:'relu' },
    { type:'MaxPool2D', size:2 },
    { type:'Flatten' },
    { type:'Dense', units:64, act:'relu' },
    { type:'Dense', units:10, act:'softmax' },
  ]);

  // ── MobileNet ─────────────────────────────────────────────────
  const loadMobileNet = async () => {
    setMnStatus('downloading'); setMnProgress(10);
    try {
      await ensureModel(MN_CDN);
      setMnProgress(60); setMnStatus('loading');
      mnRef.current = await window.mobilenet.load({ version: 2, alpha: 1.0 });
      setMnProgress(100); setMnStatus('ready');
    } catch (e) { console.error(e); setMnStatus('error'); }
  };

  const runMobileNet = async () => {
    if (!mnRef.current || !mnImgSrc) return;
    const img = document.getElementById('mn-img');
    if (!img) return;
    const t0 = performance.now();
    const preds = await mnRef.current.classify(img, 5);
    setMnTime(Math.round(performance.now() - t0));
    setMnResult(preds);
  };

  // ── COCO-SSD ──────────────────────────────────────────────────
  const loadCocoSSD = async () => {
    setCocoStatus('downloading'); setCocoProgress(10);
    try {
      await ensureModel(COCO_CDN);
      setCocoProgress(60); setCocoStatus('loading');
      cocoRef.current = await window.cocoSsd.load();
      setCocoProgress(100); setCocoStatus('ready');
    } catch (e) { console.error(e); setCocoStatus('error'); }
  };

  const runCocoSSD = async () => {
    if (!cocoRef.current || !cocoImgSrc) return;
    const img = document.getElementById('coco-img');
    if (!img) return;
    const t0 = performance.now();
    const preds = await cocoRef.current.detect(img, 10, 0.4);
    setCocoTime(Math.round(performance.now() - t0));
    setCocoResult(preds);
    // Draw bboxes
    const canvas = cocoCanvas.current;
    if (!canvas) return;
    canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    preds.forEach(({ class: label, score, bbox: [x, y, w, h] }, i) => {
      const col = P[i % P.length];
      ctx.strokeStyle = col; ctx.lineWidth = 3; ctx.strokeRect(x, y, w, h);
      ctx.fillStyle = col; ctx.fillRect(x, y - 22, label.length * 8 + 60, 22);
      ctx.fillStyle = '#000'; ctx.font = 'bold 13px monospace';
      ctx.fillText(`${label} ${(score * 100).toFixed(0)}%`, x + 4, y - 5);
    });
  };

  // ── PoseNet / MoveNet ─────────────────────────────────────────
  const loadPose = async () => {
    setPoseStatus('downloading'); setPoseProgress(10);
    try {
      await ensureModel(POSE_CDN);
      setPoseProgress(60); setPoseStatus('loading');
      const { poseDetection } = window;
      poseRef.current = await poseDetection.createDetector(
        poseDetection.SupportedModels.MoveNet,
        { modelType: poseDetection.movenet.modelType.SINGLEPOSE_LIGHTNING }
      );
      setPoseProgress(100); setPoseStatus('ready');
    } catch (e) { console.error(e); setPoseStatus('error'); }
  };

  const runPose = async () => {
    if (!poseRef.current || !poseImgSrc) return;
    const img = document.getElementById('pose-img');
    if (!img) return;
    const t0 = performance.now();
    const poses = await poseRef.current.estimatePoses(img);
    setPoseTime(Math.round(performance.now() - t0));
    if (!poses[0]) return;
    setPoseResult(poses[0].keypoints);
    // Draw
    const canvas = poseCanvas.current;
    if (!canvas) return;
    canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    drawSkeleton(canvas, poses[0].keypoints, img.naturalWidth, img.naturalHeight);
  };

  // ── CUSTOM TRAINING (pure JS demo, no TF.js needed) ───────────
  const DATASETS = {
    spiral: () => {
      const data = [];
      for (let cls = 0; cls < 2; cls++) {
        for (let i = 0; i < 50; i++) {
          const r = i / 50, angle = r * 4 * Math.PI + cls * Math.PI + (Math.random() - 0.5) * 0.3;
          data.push({ x: [r * Math.cos(angle), r * Math.sin(angle)], y: cls });
        }
      }
      return data;
    },
    xor: () => {
      const data = [];
      for (let i = 0; i < 100; i++) {
        const x0 = Math.random(), x1 = Math.random();
        data.push({ x: [x0, x1], y: (x0 > 0.5) !== (x1 > 0.5) ? 1 : 0 });
      }
      return data;
    },
    circles: () => {
      const data = [];
      for (let i = 0; i < 100; i++) {
        const r = Math.random(), a = Math.random() * 2 * Math.PI;
        const x0 = r * Math.cos(a) * 0.5 + 0.5, x1 = r * Math.sin(a) * 0.5 + 0.5;
        data.push({ x: [x0, x1], y: r < 0.4 ? 1 : 0 });
      }
      return data;
    },
  };

  useEffect(() => () => cancelAnimationFrame(ctRAF.current), []);

  const startCustomTrain = useCallback(() => {
    const data = DATASETS[ctDataset]();
    const net = new CustomNN(2, ctHidden, 2, ctLR);
    ctNetRef.current = net;
    setCtLoss([]); setCtAcc([]); setCtEpoch(0); setCtStatus('training');
    let epoch = 0;
    const lh = [], ah = [];
    const step = () => {
      let totalLoss = 0;
      // shuffle
      const shuffled = [...data].sort(() => Math.random() - 0.5);
      shuffled.forEach(({ x, y }) => {
        const yOH = [0, 0]; yOH[y] = 1;
        totalLoss += net.step(x, yOH);
      });
      epoch++;
      const acc = data.filter(({ x, y }) => { const out = net.forward(x); return out.indexOf(Math.max(...out)) === y; }).length / data.length;
      lh.push({ epoch, loss: +(totalLoss / data.length).toFixed(4) });
      ah.push({ epoch, acc: +(acc * 100).toFixed(1) });
      setCtLoss([...lh]); setCtAcc([...ah]); setCtEpoch(epoch);
      if (epoch < 500) ctRAF.current = requestAnimationFrame(step);
      else setCtStatus('ready');
    };
    ctRAF.current = requestAnimationFrame(step);
  }, [ctDataset, ctHidden, ctLR]);

  // ── LAYER BUILDER ─────────────────────────────────────────────
  const LAYER_TYPES = ['Dense','Conv2D','MaxPool2D','Flatten','Dropout','BatchNorm'];
  const addLayer = (type) => setLayerDef(ls => [...ls.slice(0,-1), { type, units: 64, filters: 32, kernel: 3, act: 'relu', size: 2, rate: 0.3 }, ls[ls.length-1]]);
  const removeLayer = (i) => { if (layerDef.length > 1) setLayerDef(ls => ls.filter((_, j) => j !== i)); };
  const totalParams = () => {
    let p = 0, prevCh = 1, prevUnits = 784;
    layerDef.forEach(l => {
      if (l.type === 'Dense') { p += (prevUnits + 1) * (l.units || 64); prevUnits = l.units || 64; }
      if (l.type === 'Conv2D') { p += (l.kernel || 3) ** 2 * prevCh * (l.filters || 32) + (l.filters || 32); prevCh = l.filters || 32; }
    });
    return p;
  };

  // ── DECISION BOUNDARY RENDER ──────────────────────────────────
  const decCanvas = useRef(null);
  useEffect(() => {
    const net = ctNetRef.current;
    if (!net || !decCanvas.current) return;
    const canvas = decCanvas.current, ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const img = ctx.createImageData(W, H);
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        const out = net.forward([x / W, y / H]);
        const v = out[1]; // probability of class 1
        const i = (y * W + x) * 4;
        img.data[i]   = v > 0.5 ? 20  : 80;
        img.data[i+1] = v > 0.5 ? 80  : 20;
        img.data[i+2] = v > 0.5 ? 50  : 20;
        img.data[i+3] = 150;
      }
    }
    ctx.putImageData(img, 0, 0);
  });

  return (
    <div className="anim-fadeup" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '3px', background: 'var(--deep)', padding: '4px', borderRadius: '3px', border: '1px solid var(--b0)', overflowX: 'auto' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '6px 14px', border: `1px solid ${tab === t ? ACCENT : 'transparent'}`, background: tab === t ? ACCENT + '18' : 'transparent', color: tab === t ? ACCENT : 'var(--t3)', fontSize: '10px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono', whiteSpace: 'nowrap', fontWeight: tab === t ? 700 : 400 }}>
            {t.replace('_',' ').toUpperCase()}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="TENSORFLOW.JS — OVERVIEW" accent={ACCENT}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.85 }}>
                <strong style={{ color: ACCENT }}>TensorFlow.js</strong> brings Google's ML framework to the browser via WebGL, WebGPU, and WASM backends. Supports both loading pre-trained models and defining/training custom architectures.<br /><br />
                Key difference from Transformers.js: TF.js provides the full tensor computation engine — you can define layers, run forward/backward passes, and train custom models from scratch. It also ships a library of production pre-trained models (MobileNet, COCO-SSD, MoveNet, BERT-QA, etc.) via separate CDN packages.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                {[
                  ['Version','4.22.0', ACCENT],
                  ['Backends','WebGL · WASM · WebGPU · CPU','var(--cyan)'],
                  ['Pre-trained models','20+ on npm','var(--green)'],
                  ['Custom training','Layers API + optimizer','var(--violet)'],
                  ['Tensor ops','400+ operations','var(--gold)'],
                  ['WebGL acceleration','50-100× over CPU','var(--orange)'],
                ].map(([k,v,c]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'var(--raised)', borderRadius: '3px' }}>
                    <span style={{ fontSize: '11px', color: 'var(--t3)' }}>{k}</span>
                    <span style={{ fontSize: '11px', color: c, fontFamily: 'JetBrains Mono', fontWeight: 700 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </Panel>
          <Panel title="PRE-TRAINED MODEL CATALOG" accent={ACCENT}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px' }}>
              {[
                { name:'MobileNet v2', task:'Image Classification', size:'~14MB', classes:1000, color:ACCENT },
                { name:'COCO-SSD',     task:'Object Detection',     size:'~27MB', classes:80,   color:'var(--cyan)' },
                { name:'MoveNet Lightning', task:'Pose Estimation', size:'~3MB',  classes:'33kp', color:'var(--green)' },
                { name:'DeepLab v3',   task:'Segmentation',         size:'~47MB', classes:21,   color:'var(--violet)' },
                { name:'BERT-QA',      task:'Question Answering',   size:'~45MB', classes:'span', color:'var(--gold)' },
                { name:'USE',          task:'Sentence Embeddings',  size:'~22MB', classes:'512d', color:'var(--orange)' },
                { name:'Toxicity',     task:'Text Classification',  size:'~30MB', classes:7,    color:'var(--red)' },
                { name:'Face Landmarks', task:'Face Detection',     size:'~3MB',  classes:'468kp','color': '#f472b6' },
                { name:'Speech Commands', task:'Audio Classification', size:'~20MB', classes:18, color:'var(--cyan)' },
              ].map(({ name, task, size, classes, color }) => (
                <div key={name} style={{ padding: '10px 12px', background: 'var(--raised)', border: `1px solid ${color}33`, borderRadius: '3px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color, marginBottom: '3px' }}>{name}</div>
                  <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '3px' }}>{task}</div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <Tag color={color}>{size}</Tag>
                    <Tag color="var(--t3)">{classes} classes</Tag>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── MOBILENET ── */}
      {tab === 'mobilenet' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="MOBILENET V2 — 1000-CLASS IMAGE CLASSIFICATION" accent={ACCENT}>
            <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.7, marginBottom: '14px' }}>
              <strong style={{ color: ACCENT }}>MobileNet v2</strong> is a lightweight CNN architecture designed for mobile/browser inference. Depthwise separable convolutions reduce params vs standard Conv2D. Trained on ImageNet (1000 classes). Accuracy: top-1 71.8%, top-5 91%.
            </div>
            {mnStatus !== 'ready' && (
              <ModelLoader status={mnStatus} progress={mnProgress} modelName="mobilenet-v2" modelSize="~14MB" accent={ACCENT} onLoad={loadMobileNet} />
            )}
          </Panel>
          {(mnStatus === 'ready') && (
            <Panel title="CLASSIFY IMAGE" accent={ACCENT}>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <label style={{ padding: '8px 16px', background: ACCENT + '18', border: `1px solid ${ACCENT}`, color: ACCENT, fontSize: '10px', cursor: 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>
                  📁 UPLOAD IMAGE
                  <input type="file" accept="image/*" onChange={e => { const f = e.target.files[0]; if (f) setMnImgSrc(URL.createObjectURL(f)); }} style={{ display: 'none' }} />
                </label>
                {mnImgSrc && <Btn onClick={runMobileNet} color={ACCENT}>CLASSIFY →</Btn>}
                {mnTime && <InferenceTag ms={mnTime} />}
              </div>
              {mnImgSrc && (
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '16px', alignItems: 'start' }}>
                  <img id="mn-img" src={mnImgSrc} alt="input" style={{ maxWidth: '240px', maxHeight: '240px', borderRadius: '4px', border: '1px solid var(--b0)', display: 'block' }} crossOrigin="anonymous" />
                  {mnResult && (
                    <div>
                      <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '8px', letterSpacing: '.1em' }}>TOP-5 PREDICTIONS</div>
                      {mnResult.map(({ className, probability }, i) => (
                        <div key={className} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 10px', background: 'var(--raised)', borderRadius: '3px', marginBottom: '5px', border: i === 0 ? `1px solid ${ACCENT}44` : '1px solid var(--b0)' }}>
                          <span style={{ fontSize: '9px', color: 'var(--t3)', fontFamily: 'JetBrains Mono', minWidth: '18px' }}>#{i+1}</span>
                          <span style={{ fontSize: '12px', color: i === 0 ? ACCENT : 'var(--t1)', fontWeight: i === 0 ? 700 : 400, flex: 1 }}>{className}</span>
                          <div style={{ width: '100px', height: '5px', background: 'var(--deep)', borderRadius: '3px' }}>
                            <div style={{ height: '100%', width: `${probability * 100}%`, background: P[i], borderRadius: '3px' }} />
                          </div>
                          <span style={{ fontSize: '11px', color: P[i], fontFamily: 'JetBrains Mono', fontWeight: 700, minWidth: '42px' }}>{(probability * 100).toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Panel>
          )}
        </div>
      )}

      {/* ── COCO-SSD ── */}
      {tab === 'coco_ssd' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="COCO-SSD — OBJECT DETECTION (80 CLASSES)" accent="var(--cyan)">
            <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.7, marginBottom: '14px' }}>
              <strong style={{ color: 'var(--cyan)' }}>COCO-SSD</strong> detects 80 object categories (COCO dataset: person, car, dog, chair…) with bounding boxes and confidence scores. SSD (Single Shot Detector) trades accuracy for speed — runs at ~100ms in WebGL.
            </div>
            {cocoStatus !== 'ready' && (
              <ModelLoader status={cocoStatus} progress={cocoProgress} modelName="coco-ssd" modelSize="~27MB" accent="var(--cyan)" onLoad={loadCocoSSD} />
            )}
          </Panel>
          {cocoStatus === 'ready' && (
            <Panel title="DETECT OBJECTS" accent="var(--cyan)">
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <label style={{ padding: '8px 16px', background: 'var(--cyan)18', border: '1px solid var(--cyan)', color: 'var(--cyan)', fontSize: '10px', cursor: 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>
                  📁 UPLOAD IMAGE
                  <input type="file" accept="image/*" onChange={e => { const f = e.target.files[0]; if (f) setCocoImgSrc(URL.createObjectURL(f)); setCocoResult(null); }} style={{ display: 'none' }} />
                </label>
                {cocoImgSrc && <Btn onClick={runCocoSSD} color="var(--cyan)">DETECT →</Btn>}
                {cocoTime && <InferenceTag ms={cocoTime} />}
              </div>
              {cocoImgSrc && (
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '14px', alignItems: 'start' }}>
                  <div>
                    <img id="coco-img" src={cocoImgSrc} alt="input" style={{ display: 'none' }} crossOrigin="anonymous" />
                    <canvas ref={cocoCanvas} style={{ maxWidth: '360px', borderRadius: '4px', border: '1px solid var(--b0)', display: 'block' }} />
                  </div>
                  {cocoResult && (
                    <div>
                      <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '8px', letterSpacing: '.1em' }}>{cocoResult.length} OBJECTS DETECTED</div>
                      {cocoResult.map(({ class: label, score }, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 10px', background: 'var(--raised)', borderRadius: '3px', marginBottom: '4px' }}>
                          <span style={{ fontSize: '12px', color: P[i % P.length], fontWeight: 700, flex: 1, fontFamily: 'JetBrains Mono' }}>{label}</span>
                          <span style={{ fontSize: '11px', color: 'var(--t3)', fontFamily: 'JetBrains Mono' }}>{(score * 100).toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Panel>
          )}
        </div>
      )}

      {/* ── POSENET ── */}
      {tab === 'posenet' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="MOVENET LIGHTNING — POSE ESTIMATION (33 KEYPOINTS)" accent="var(--green)">
            <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.7, marginBottom: '14px' }}>
              <strong style={{ color: 'var(--green)' }}>MoveNet Lightning</strong> estimates 33 body keypoints at up to 50fps. Ultra-fast model (3MB) optimized for real-time. Outputs x/y coordinates + confidence score per joint.
            </div>
            {poseStatus !== 'ready' && (
              <ModelLoader status={poseStatus} progress={poseProgress} modelName="movenet-lightning" modelSize="~3MB" accent="var(--green)" onLoad={loadPose} />
            )}
          </Panel>
          {poseStatus === 'ready' && (
            <Panel title="ESTIMATE POSE" accent="var(--green)">
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <label style={{ padding: '8px 16px', background: 'var(--green)18', border: '1px solid var(--green)', color: 'var(--green)', fontSize: '10px', cursor: 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>
                  📁 UPLOAD IMAGE
                  <input type="file" accept="image/*" onChange={e => { const f = e.target.files[0]; if (f) setPoseImgSrc(URL.createObjectURL(f)); setPoseResult(null); }} style={{ display: 'none' }} />
                </label>
                {poseImgSrc && <Btn onClick={runPose} color="var(--green)">ESTIMATE POSE →</Btn>}
                {poseTime && <InferenceTag ms={poseTime} />}
              </div>
              {poseImgSrc && (
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '14px', alignItems: 'start' }}>
                  <div>
                    <img id="pose-img" src={poseImgSrc} alt="input" style={{ display: 'none' }} crossOrigin="anonymous" />
                    <canvas ref={poseCanvas} style={{ maxWidth: '360px', borderRadius: '4px', border: '1px solid var(--b0)', display: 'block' }} />
                  </div>
                  {poseResult && (
                    <div>
                      <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '8px', letterSpacing: '.1em' }}>KEYPOINTS ({poseResult.filter(k => k.score > 0.3).length} visible)</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                        {poseResult.slice(0, 16).map(kp => (
                          <div key={kp.name} style={{ display: 'flex', gap: '8px', padding: '4px 8px', background: 'var(--raised)', borderRadius: '2px', opacity: kp.score > 0.3 ? 1 : 0.35 }}>
                            <span style={{ fontSize: '9px', fontFamily: 'JetBrains Mono', color: kp.score > 0.6 ? 'var(--green)' : kp.score > 0.3 ? 'var(--gold)' : 'var(--t3)', flex: 1 }}>{kp.name}</span>
                            <span style={{ fontSize: '9px', color: 'var(--t3)', fontFamily: 'JetBrains Mono' }}>{(kp.score * 100).toFixed(0)}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Panel>
          )}
        </div>
      )}

      {/* ── CUSTOM TRAIN ── */}
      {tab === 'custom_train' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="LIVE TRAINING — 2D CLASSIFICATION" accent={ACCENT}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '14px' }}>
              <div>
                <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '6px', letterSpacing: '.1em' }}>DATASET</div>
                {['spiral','xor','circles'].map(d => (
                  <button key={d} onClick={() => setCtDataset(d)} style={{ display: 'block', width: '100%', padding: '7px 10px', border: `1px solid ${ctDataset === d ? ACCENT : 'var(--b0)'}`, background: ctDataset === d ? ACCENT + '18' : 'var(--raised)', color: ctDataset === d ? ACCENT : 'var(--t2)', fontSize: '10px', cursor: 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', marginBottom: '4px', textAlign: 'left', fontWeight: ctDataset === d ? 700 : 400 }}>{d}</button>
                ))}
              </div>
              <div>
                <div style={{ fontSize: '10px', color: 'var(--t3)', marginBottom: '5px' }}>Hidden neurons: {ctHidden}</div>
                <input type="range" min={2} max={32} value={ctHidden} onChange={e => setCtHidden(+e.target.value)} style={{ width: '100%', accentColor: ACCENT }} />
                <div style={{ fontSize: '10px', color: 'var(--t3)', marginTop: '10px', marginBottom: '5px' }}>Learning rate: {ctLR}</div>
                <input type="range" min={0.01} max={0.5} step={0.01} value={ctLR} onChange={e => setCtLR(+e.target.value)} style={{ width: '100%', accentColor: ACCENT }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', justifyContent: 'center' }}>
                <Btn onClick={startCustomTrain} color={ctStatus === 'training' ? 'var(--orange)' : ACCENT}>
                  {ctStatus === 'training' ? '⟳ TRAINING…' : '▶ TRAIN (500 epochs)'}
                </Btn>
                <Tag>{ctEpoch} epochs</Tag>
                {ctLoss.length > 0 && <Tag color="var(--green)">loss: {ctLoss[ctLoss.length-1]?.loss.toFixed(4)}</Tag>}
                {ctAcc.length > 0 && <Tag color="var(--cyan)">acc: {ctAcc[ctAcc.length-1]?.acc.toFixed(1)}%</Tag>}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr 1fr', gap: '14px', alignItems: 'start' }}>
              <div>
                <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '6px', letterSpacing: '.1em' }}>DECISION BOUNDARY</div>
                <canvas ref={decCanvas} width={180} height={180} style={{ borderRadius: '4px', border: '1px solid var(--b0)', display: 'block' }} />
                <div style={{ fontSize: '9px', color: 'var(--t3)', marginTop: '4px' }}>
                  <span style={{ color: '#4ade80' }}>■</span> class 1  <span style={{ color: '#ef4444' }}>■</span> class 0
                </div>
              </div>
              <div>
                <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '6px', letterSpacing: '.1em' }}>TRAINING LOSS</div>
                {ctLoss.length > 1 && (
                  <ResponsiveContainer width="100%" height={160}>
                    <LineChart data={ctLoss}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                      <XAxis dataKey="epoch" stroke="var(--t3)" tick={{ fontSize: 8 }} />
                      <YAxis stroke="var(--t3)" tick={{ fontSize: 8 }} />
                      <Tooltip formatter={v => [v, 'cross-entropy']} />
                      <Line type="monotone" dataKey="loss" stroke={ACCENT} strokeWidth={2} dot={false} isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
              <div>
                <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '6px', letterSpacing: '.1em' }}>ACCURACY</div>
                {ctAcc.length > 1 && (
                  <ResponsiveContainer width="100%" height={160}>
                    <LineChart data={ctAcc}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                      <XAxis dataKey="epoch" stroke="var(--t3)" tick={{ fontSize: 8 }} />
                      <YAxis stroke="var(--t3)" tick={{ fontSize: 8 }} domain={[0,100]} />
                      <Tooltip formatter={v => [`${v}%`, 'accuracy']} />
                      <Line type="monotone" dataKey="acc" stroke="var(--green)" strokeWidth={2} dot={false} isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </Panel>
        </div>
      )}

      {/* ── TRANSFER ── */}
      {tab === 'transfer' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="TRANSFER LEARNING CONCEPT" accent={ACCENT}>
            <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.85 }}>
              Transfer learning reuses a pretrained model's feature extraction layers (frozen) and replaces the final classifier head with a new one trained on your data. MobileNet trained on ImageNet already knows edges, textures, shapes — you just need to teach it your specific categories with far less data and compute.
            </div>
          </Panel>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {[
              { step:'1', title:'Pretrained Base', color:ACCENT, desc:'Load MobileNet. Freeze all convolutional layers — their weights stay fixed. These layers act as a powerful feature extractor, converting any image into a 1280-dimensional feature vector.' },
              { step:'2', title:'Collect Samples', color:'var(--cyan)', desc:'For each custom class, upload 5–20 example images. MobileNet converts each to a 1280d feature vector. These become your training data for the new classifier head.' },
              { step:'3', title:'Train New Head', color:'var(--green)', desc:'Add a small Dense layer on top (128 → N_classes). Train only this layer on your feature vectors. With 10+ samples per class, training takes <1 second. Accuracy typically 85-95%.' },
              { step:'4', title:'Classify New Images', color:'var(--violet)', desc:'Upload any new image → MobileNet extracts features → your custom head classifies. The classification reflects your custom categories, not the original 1000 ImageNet classes.' },
            ].map(({ step, title, color, desc }) => (
              <div key={step} style={{ padding: '14px', background: 'var(--raised)', border: `1px solid ${color}33`, borderRadius: '4px' }}>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '8px', alignItems: 'center' }}>
                  <div style={{ width: '24px', height: '24px', background: color + '20', border: `1px solid ${color}44`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color, fontFamily: 'JetBrains Mono', flexShrink: 0 }}>{step}</div>
                  <span style={{ fontSize: '12px', fontWeight: 700, color }}>{title}</span>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.7 }}>{desc}</div>
              </div>
            ))}
          </div>
          <Panel title="TEACHABLE MACHINE PATTERN (code)" accent={ACCENT}>
            <pre style={{ margin: 0, padding: '14px', background: 'var(--deep)', border: '1px solid var(--b0)', borderRadius: '3px', fontSize: '10px', color: 'var(--t2)', fontFamily: 'JetBrains Mono', lineHeight: 1.7, overflowX: 'auto' }}>{`// 1. Load MobileNet as feature extractor
const mobilenet = await window.mobilenet.load();
const base = mobilenet.model;
const cutLayer = base.getLayer('conv_pw_13_relu');
const featureModel = tf.model({ inputs: base.inputs, outputs: cutLayer.output });

// 2. Collect training data
const samples = []; const labels = [];
// For each image:
const features = featureModel.predict(tf.browser.fromPixels(imgEl).expandDims(0));
samples.push(features.flatten());
labels.push(classIdx);

// 3. Train new head
const classifier = tf.sequential();
classifier.add(tf.layers.dense({ inputShape: [1280], units: 128, activation: 'relu' }));
classifier.add(tf.layers.dense({ units: numClasses, activation: 'softmax' }));
classifier.compile({ optimizer: 'adam', loss: 'categoricalCrossentropy' });
await classifier.fit(tf.stack(samples), tf.oneHot(labels, numClasses), { epochs: 30 });

// 4. Classify new image
const testFeatures = featureModel.predict(tf.browser.fromPixels(newImg).expandDims(0)).flatten();
const prediction = classifier.predict(testFeatures.expandDims(0));`}</pre>
          </Panel>
        </div>
      )}

      {/* ── LAYERS ── */}
      {tab === 'layers' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="INTERACTIVE LAYER BUILDER" accent={ACCENT}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '10px', letterSpacing: '.1em' }}>ARCHITECTURE</div>
                {layerDef.map((layer, i) => (
                  <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px', padding: '8px 10px', background: 'var(--raised)', border: `1px solid ${P[i % P.length]}33`, borderRadius: '3px' }}>
                    <div style={{ width: '8px', height: '8px', background: P[i % P.length], borderRadius: '50%', flexShrink: 0 }} />
                    <span style={{ fontSize: '11px', color: P[i % P.length], fontFamily: 'JetBrains Mono', fontWeight: 700, minWidth: '90px' }}>{layer.type}</span>
                    <span style={{ fontSize: '10px', color: 'var(--t3)', flex: 1 }}>
                      {layer.units ? `units=${layer.units}` : ''}
                      {layer.filters ? `filters=${layer.filters}` : ''}
                      {layer.size ? `pool=${layer.size}` : ''}
                      {layer.act ? ` act=${layer.act}` : ''}
                    </span>
                    <button onClick={() => removeLayer(i)} style={{ background: 'transparent', border: '1px solid var(--red)33', color: 'var(--red)', cursor: 'pointer', borderRadius: '2px', width: '20px', height: '20px', fontSize: '10px', flexShrink: 0 }}>×</button>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginTop: '10px' }}>
                  {LAYER_TYPES.map(t => (
                    <button key={t} onClick={() => addLayer(t)} style={{ padding: '4px 10px', border: `1px solid ${ACCENT}44`, background: ACCENT + '10', color: ACCENT, fontSize: '9px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono' }}>+{t}</button>
                  ))}
                </div>
                <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <Tag>{layerDef.length} layers</Tag>
                  <Tag color="var(--gold)">~{totalParams().toLocaleString()} params</Tag>
                </div>
              </div>
              <div>
                <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '10px', letterSpacing: '.1em' }}>TF.JS CODE</div>
                <pre style={{ margin: 0, padding: '12px', background: 'var(--deep)', border: '1px solid var(--b0)', borderRadius: '3px', fontSize: '10px', color: 'var(--t2)', fontFamily: 'JetBrains Mono', lineHeight: 1.7, overflowX: 'auto' }}>
                  {`const model = tf.sequential();\n` + layerDef.map(l => {
                    if (l.type === 'Dense')      return `model.add(tf.layers.dense({ units: ${l.units||64}, activation: '${l.act||'relu'}' }));`;
                    if (l.type === 'Conv2D')     return `model.add(tf.layers.conv2d({ filters: ${l.filters||32}, kernelSize: ${l.kernel||3}, activation: '${l.act||'relu'}' }));`;
                    if (l.type === 'MaxPool2D')  return `model.add(tf.layers.maxPooling2d({ poolSize: ${l.size||2} }));`;
                    if (l.type === 'Flatten')    return `model.add(tf.layers.flatten());`;
                    if (l.type === 'Dropout')    return `model.add(tf.layers.dropout({ rate: ${l.rate||0.3} }));`;
                    if (l.type === 'BatchNorm')  return `model.add(tf.layers.batchNormalization());`;
                    return '';
                  }).join('\n')}
                </pre>
              </div>
            </div>
          </Panel>
        </div>
      )}

      {/* ── CONCEPTS ── */}
      {tab === 'concepts' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {[
            { title:'WebGL Backend', color:ACCENT, desc:'TF.js compiles tensor ops to WebGL fragment shaders. Each shader processes thousands of values in parallel on the GPU. Matrix multiplication (the core of NN inference) maps naturally to texture operations. 50-100× faster than CPU for large tensors. No WebGPU required — works on any GPU with WebGL 2.0 support.' },
            { title:'Tensor Memory Management', color:'var(--orange)', desc:'JavaScript garbage collection doesn\'t know about GPU memory. TF.js requires explicit cleanup: use tf.tidy(() => { ... }) to auto-dispose tensors, or tensor.dispose() manually. Memory leaks are the #1 issue in TF.js apps — tf.memory() shows allocations. Always wrap inference in tf.tidy().' },
            { title:'MobileNet Architecture', color:'var(--cyan)', desc:'Depthwise separable convolution = depthwise conv (per-channel spatial filtering) + pointwise conv (1×1 cross-channel mixing). 8-9× fewer multiplications than standard Conv2D for same accuracy. Width multiplier α (0.25–1.0) controls speed/accuracy tradeoff. Designed by Howard et al. 2017 at Google.' },
            { title:'Transfer Learning Intuition', color:'var(--green)', desc:'Early CNN layers learn low-level features (edges, corners, textures) that generalize across all image domains. Only the final layers are task-specific. Freezing the base and fine-tuning the head works because these general features transfer — a dog detector\'s early layers also work for car detection, medical imaging, satellite imagery.' },
            { title:'Custom Training Pipeline', color:'var(--violet)', desc:'tf.sequential() or tf.model() → add layers → model.compile({optimizer, loss}) → model.fit(xTensor, yTensor, {epochs, batchSize, callbacks}). Optimizer options: sgd, adam, rmsprop, adagrad. Loss: meanSquaredError, categoricalCrossentropy, binaryCrossentropy. Callbacks: onEpochEnd for live progress.' },
            { title:'Pose Estimation Architecture', color:'var(--gold)', desc:'MoveNet uses a feature pyramid + hourglass architecture. Input: 192×192 or 256×256 RGB. Output: 17 or 33 keypoints × (x, y, confidence). Trained with heatmap supervision + offset regression. Initialization matters: network predicts all joints from bottom-up, then refines. Real-time at 50fps on phones.' },
          ].map(({ title, color, desc }) => (
            <div key={title} style={{ padding: '16px', background: 'var(--raised)', border: `1px solid ${color}33`, borderRadius: '4px' }}>
              <div style={{ color, fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>{title}</div>
              <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.75 }}>{desc}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
